import React, { Component, Fragment } from 'react';
import Header from './Header';
import moment from 'moment';
import 'react-table/react-table.css';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import {
  TextField,
  Button,
  Snackbar,
  LinearProgress,
  Dialog,
  DialogContent,
} from '@material-ui/core';
import { getAllCostCenters, getCostOwnerCenterReport } from '../utils/PaymentRequestApiUtils';
import Table from './Table';
import CustomMultiSelectDropdown from './CustomMultiSelectDropdown';

const TABLE_COLUMNS = [
  { Header: 'Payment Request ID', key: 'payment_request_id' },
  { Header: 'Payment Created At', key: 'payment_created_at' },
  { Header: 'Center Name', key: 'center_name' },
  { Header: 'Ledger Name', key: 'ledger_name' },
  { Header: 'Payable Amount', key: 'payable_amount' },
  { Header: 'Cost To Owner', key: 'cost_to_owner_display' },
  { Header: 'Cost To Center', key: 'cost_to_center' }
];

class CostOwnerCenterReport extends Component {
  constructor() {
    super();
    this.isUnmounted = false;
    this.state = {
      data: [],
      columns: TABLE_COLUMNS,
      centerOptions: [],
      selectedCenterIds: [],
      fromDate: '',
      toDate: '',
      inProgress: false,
      snackOpen: false,
      snackMessage: '',
      hasSubmittedFilters: false
    };
  }

  componentDidMount() {
    this.loadCenters();
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  loadCenters = () => {
    this.setState({ inProgress: true }, () => {
      getAllCostCenters().then((response) => {
        if (this.isUnmounted) return;
        if (response && response.status === 'success') {
          const centerOptions = (response.data && response.data.cost_centers ? response.data.cost_centers : []).map((center) => ({
            id: center.Center_ID,
            label: `${center.LOCALNAME} (${center.Type}), ${center.CITY}`
          }));
          this.setState({ centerOptions, inProgress: false });
        } else {
          this.setState({
            inProgress: false,
            snackOpen: true,
            snackMessage: response && response.message ? response.message : 'Unable to fetch centers'
          });
        }
      }).catch(() => {
        if (this.isUnmounted) return;
        this.setState({
          inProgress: false,
          snackOpen: true,
          snackMessage: 'Unable to fetch centers'
        });
      });
    });
  }

  handleDateChange = (key) => (event) => {
    this.setState({ [key]: event.target.value });
  }

  handleCenterIdsChange = (nextSelectedIds) => {
    this.setState({ selectedCenterIds: nextSelectedIds || [] });
  }

  validateFilters = () => {
    const { fromDate, toDate, selectedCenterIds } = this.state;
    if (!fromDate || !toDate) {
      this.setState({ snackOpen: true, snackMessage: 'Please select both from and to dates' });
      return false;
    }
    if (fromDate > toDate) {
      this.setState({ snackOpen: true, snackMessage: 'From date should be less than or equal to To date' });
      return false;
    }
    if (!selectedCenterIds.length) {
      this.setState({ snackOpen: true, snackMessage: 'Please select at least one center' });
      return false;
    }
    return true;
  }

  submitFilter = () => {
    if (!this.validateFilters()) return;
    this.setState({ hasSubmittedFilters: true }, () => this.fetchReportData());
  }

  clearFilter = () => {
    this.setState({
      data: [],
      selectedCenterIds: [],
      fromDate: '',
      toDate: '',
      hasSubmittedFilters: false
    });
  }

  fetchReportData = () => {
    const { fromDate, toDate, selectedCenterIds } = this.state;
    const payload = {
      from_date: fromDate,
      to_date: toDate,
      center_ids: selectedCenterIds.join(','),
    };

    this.setState({ inProgress: true }, () => {
      getCostOwnerCenterReport(payload).then((response) => {
        if (this.isUnmounted) return;
        if (response && response.status === 'success') {
          const selectedSet = new Set((selectedCenterIds || []).map((id) => String(id)));
          const formattedData = (response.data || [])
            .filter((row) => selectedSet.has(String(row.center_id)))
            .map((row) => ({
            ...row,
            payment_created_at: row.payment_created_at
              ? moment(row.payment_created_at).format('DD-MM-YYYY')
              : '',
            cost_to_owner_display: `${row.cost_to_owner} (${Number.parseInt(row.recoverable_percent, 10) || 0}%)`
          }));
          this.setState({
            data: formattedData,
            inProgress: false
          });
        } else {
          this.setState({
            inProgress: false,
            snackOpen: true,
            snackMessage: response && response.message ? response.message : 'Unable to fetch report data'
          });
        }
      }).catch(() => {
        if (this.isUnmounted) return;
        this.setState({
          inProgress: false,
          snackOpen: true,
          snackMessage: 'Unable to fetch report data'
        });
      });
    });
  }

  handleCloseSnack = () => {
    this.setState({ snackOpen: false });
  }

  render() {
    return (
      <Fragment>
        <MuiThemeProvider>
          <Header />
        <div className="zaps-page-wrap">
          <div className="zaps-page-header">
            <div>
              <h2 className="zaps-page-title">Cost Owner / Center Report</h2>
              <p className="zaps-page-subtitle">Cost center allocation and ownership analytics</p>
            </div>
          </div>
          <h4 style={{ textAlign: 'center' }}>
            Cost To Owner/Center Report
          </h4>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '20px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="From Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={this.state.fromDate}
                onChange={this.handleDateChange('fromDate')}
                style={{ marginRight: '20px', minWidth: '160px' }}
                margin='normal'
              />
              <TextField
                label="To Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={this.state.toDate}
                onChange={this.handleDateChange('toDate')}
                style={{ marginRight: '20px', minWidth: '160px' }}
                margin='normal'
              />
              <div style={{ marginRight: '20px', marginTop: '16px', width: '320px' }}>
                <CustomMultiSelectDropdown
                  label="Select Centers"
                  placeholder="Search & select"
                  options={this.state.centerOptions}
                  selectedIds={this.state.selectedCenterIds}
                  onChange={this.handleCenterIdsChange}
                />
              </div>
              <Button
                variant="contained"
                color="primary"
                onClick={this.submitFilter}
                style={{ marginRight: '20px', marginTop: '18px' }}
              >
                Submit Filter
              </Button>
              <Button
                variant="contained"
                onClick={this.clearFilter}
                style={{ marginTop: '18px', background: 'red', color: 'white' }}
              >
                Clear
              </Button>
            </div>
            <div style={{ marginTop: '20px' }}>
              Total Records: {this.state.data.length}
            </div>
          </div>
          <Table columns={this.state.columns} data={this.state.data} />
          <Dialog open={this.state.inProgress}>
            <DialogContent>
              Loading...
              <br /><br />
              <LinearProgress />
            </DialogContent>
          </Dialog>
          <Snackbar
            open={this.state.snackOpen}
            message={this.state.snackMessage}
            autoHideDuration={2500}
            onClose={this.handleCloseSnack}
          />
      
        </div>{/* end zaps-page-wrap */}
      </MuiThemeProvider>
          </Fragment>
    );
  }
}

export default CostOwnerCenterReport;



// WEBPACK FOOTER //
// ./src/components/CostOwnerCenterReport.js